import React, { useEffect, useMemo, useState } from 'react'
import { Modal, Button, Table, Form } from 'react-bootstrap'
import { format } from 'date-fns'
import { toast } from 'react-toastify'
import apinode from '../../services/apinode'
import { CustomButton } from '../../components/CustomButton'

const fmt = (valor, padrao) => {
  if (!valor) return '—'
  const d = new Date(valor)
  return isNaN(d.getTime()) ? '—' : format(d, padrao)
}

const descDe = (item) => item.descricao ?? item.DESCRICAO ?? '—'

const ModalTable = ({ data, showModal, handleClose }) => {
  const [devolv, setDevolv] = useState([])
  const [isDevolvLoading, setIsDevolvLoading] = useState({})
  const [busca, setBusca] = useState('')
  const [selecionados, setSelecionados] = useState(new Set())
  const [confirmLista, setConfirmLista] = useState(null) // itens (já normalizados) aguardando confirmação
  const [processando, setProcessando] = useState(false)

  useEffect(() => {
    setDevolv(
      (Array.isArray(data) ? data : []).map((item) => ({
        ...item,
        vai_devolver: item.vai_devolver ?? 0,
      })),
    )
    setSelecionados(new Set())
  }, [data])

  useEffect(() => {
    if (!showModal) {
      setConfirmLista(null)
      setSelecionados(new Set())
    }
  }, [showModal])

  const setVaiDevolver = (id, value) =>
    setDevolv((prev) =>
      prev.map((it) => (it.id === id ? { ...it, vai_devolver: value } : it)),
    )

  const saldoDe = (item) =>
    Number(item.qtde) - (Number(item.qtde_devolvida) || 0)

  // quantidade usada no estorno em lote: vazio => saldo total; senão clampa no saldo
  const qtdBulk = (item) => {
    const saldo = saldoDe(item)
    const vd = Number(item.vai_devolver)
    if (!vd || vd < 1) return saldo
    return Math.min(vd, saldo)
  }

  // PUT de um item (sem reload — o reload é feito uma vez no fim)
  const estornarUm = async (item) => {
    const { id, id_pedido, sku, vai_devolver } = item
    try {
      setIsDevolvLoading((prev) => ({ ...prev, [id]: true }))
      const r = await apinode.put(
        `/devolucoes/dev/parcial/${localStorage.idconta}`,
        { qtd: vai_devolver, sku, pedido: id_pedido },
      )
      return { ok: true, msg: r.data?.message }
    } catch (error) {
      console.error('Erro ao solicitar estorno:', error)
      return { ok: false, sku }
    } finally {
      setIsDevolvLoading((prev) => ({ ...prev, [id]: false }))
    }
  }

  const load = async (id_pedido) => {
    try {
      const response = await apinode.get(
        `/devolucoes/listar/${id_pedido}/${localStorage.idconta}`,
      )
      const frescos = response.data.map((item) => ({ ...item, vai_devolver: 0 }))
      const byId = new Map(frescos.map((f) => [f.id, f]))
      setDevolv((prev) => {
        const atualizados = prev.map((it) => byId.get(it.id) ?? it)
        const idsExistentes = new Set(prev.map((i) => i.id))
        const novos = frescos.filter((f) => !idsExistentes.has(f.id))
        return [...atualizados, ...novos]
      })
    } catch {
      toast.error('Erro ao realizar solicitação.')
    }
  }

  // abre a confirmação com 1 ou N itens (já normalizados)
  const pedirConfirmacao = (itens) => setConfirmLista(itens)
  const cancelarConfirmacao = () => !processando && setConfirmLista(null)

  const confirmarEstorno = async () => {
    const lista = confirmLista
    if (!lista?.length) return
    setProcessando(true)

    const pedidos = new Set()
    let ok = 0
    const erros = []
    for (const it of lista) {
      const r = await estornarUm(it)
      if (r.ok) {
        ok++
        pedidos.add(it.id_pedido)
      } else {
        erros.push(r.sku)
      }
    }
    for (const p of pedidos) await load(p)

    setProcessando(false)
    setConfirmLista(null)
    setSelecionados(new Set())
    if (ok) toast.success(`${ok} devolução(ões) realizada(s) com sucesso.`)
    if (erros.length) toast.error(`Falha em: ${erros.join(', ')}`)
  }

  const fecharTudo = () => {
    if (processando) return
    setConfirmLista(null)
    setSelecionados(new Set())
    handleClose()
  }

  const matchBusca = (item) => {
    const q = busca.trim().toLowerCase()
    if (!q) return true
    return (
      String(item.id ?? '').toLowerCase().includes(q) ||
      String(item.sku ?? '').toLowerCase().includes(q) ||
      String(descDe(item)).toLowerCase().includes(q) ||
      String(item.id_pedido ?? '').toLowerCase().includes(q)
    )
  }

  const aDevolver = useMemo(
    () => devolv.filter((i) => saldoDe(i) > 0 && matchBusca(i)),
    [devolv, busca],
  )
  const jaDevolvidos = useMemo(
    () => devolv.filter((i) => (Number(i.qtde_devolvida) || 0) > 0 && matchBusca(i)),
    [devolv, busca],
  )

  const totais = useMemo(() => {
    const saldoAberto = aDevolver.reduce((s, i) => s + saldoDe(i), 0)
    const totalDevolvido = jaDevolvidos.reduce(
      (s, i) => s + (Number(i.qtde_devolvida) || 0),
      0,
    )
    return { saldoAberto, totalDevolvido }
  }, [aDevolver, jaDevolvidos])

  // seleção múltipla (só itens visíveis com saldo)
  const idsADevolver = aDevolver.map((i) => i.id)
  const selecionadosValidos = aDevolver.filter((i) => selecionados.has(i.id))
  const todosSelecionados =
    idsADevolver.length > 0 && idsADevolver.every((id) => selecionados.has(id))

  const toggleTodos = () =>
    setSelecionados(todosSelecionados ? new Set() : new Set(idsADevolver))
  const toggleUm = (id) =>
    setSelecionados((prev) => {
      const n = new Set(prev)
      n.has(id) ? n.delete(id) : n.add(id)
      return n
    })

  const abrirConfirmSelecionados = () =>
    pedirConfirmacao(
      selecionadosValidos.map((i) => ({ ...i, vai_devolver: qtdBulk(i) })),
    )

  const totalUnidadesConfirm = (confirmLista || []).reduce(
    (s, i) => s + (Number(i.vai_devolver) || 0),
    0,
  )

  return (
    <Modal show={showModal} onHide={fecharTudo} dialogClassName="custom-modalzpl">
      <Modal.Header closeButton>
        <Modal.Title>Lista de Itens</Modal.Title>
      </Modal.Header>

      <Modal.Body style={{ position: 'relative' }}>
        {/* ══ Toolbar: busca + totais ══════════════════════════════ */}
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginBottom: 16 }}>
          <div style={{ position: 'relative', flex: '1 1 260px', maxWidth: 340 }}>
            <Form.Control
              size="sm"
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              placeholder="Buscar por código, SKU, descrição ou pedido..."
              style={{ paddingRight: 28 }}
            />
            {busca && (
              <span
                onClick={() => setBusca('')}
                title="Limpar"
                style={{
                  position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
                  cursor: 'pointer', color: '#94a3b8', fontWeight: 700, lineHeight: 1,
                }}
              >
                ×
              </span>
            )}
          </div>

          <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ ...chip, background: '#fef3c7', color: '#92400e' }}>
              A devolver: {totais.saldoAberto} un.
            </span>
            <span style={{ ...chip, background: '#dcfce7', color: '#166534' }}>
              Devolvido: {totais.totalDevolvido} un.
            </span>
          </div>
        </div>

        {/* ══ Seção: A DEVOLVER ══════════════════════════════════════ */}
        <div style={secHead}>
          <span style={{ ...dot, background: '#f59e0b' }} />
          <span style={secTitle}>Itens a devolver</span>
          <span style={badge}>{aDevolver.length}</span>
        </div>

        {/* barra de ação em lote */}
        {selecionadosValidos.length > 0 && (
          <div style={bulkBar}>
            <span style={{ fontSize: 13, fontWeight: 600, color: '#1e293b' }}>
              {selecionadosValidos.length} selecionado(s)
            </span>
            <Button variant="link" size="sm" style={{ textDecoration: 'none' }} onClick={() => setSelecionados(new Set())}>
              limpar seleção
            </Button>
            <div style={{ marginLeft: 'auto' }}>
              <CustomButton variant="outline-danger" size="sm" isDelete onClick={abrirConfirmSelecionados}>
                Estornar selecionados
              </CustomButton>
            </div>
          </div>
        )}

        <Table striped bordered hover size="sm" className="mb-4">
          <thead style={thead}>
            <tr>
              <th style={{ width: 34, textAlign: 'center' }}>
                <Form.Check
                  type="checkbox"
                  checked={todosSelecionados}
                  onChange={toggleTodos}
                  title="Selecionar todos"
                />
              </th>
              <th>Código</th>
              <th>SKU</th>
              <th>Descrição</th>
              <th>Pedido</th>
              <th>Data</th>
              <th>Hora</th>
              <th>Qtd</th>
              <th>Já devolvida</th>
              <th>Saldo</th>
              <th>Vai Devolver</th>
              <th>Ação</th>
            </tr>
          </thead>
          <tbody>
            {aDevolver.length === 0 ? (
              <tr>
                <td colSpan={12} style={vazio}>
                  {busca ? 'Nenhum item encontrado para essa busca.' : 'Nenhum item disponível para devolução.'}
                </td>
              </tr>
            ) : (
              aDevolver.map((item) => {
                const saldo = saldoDe(item)
                const vd = Number(item.vai_devolver)
                const invalido = !vd || vd < 1 || vd > saldo
                const marcado = selecionados.has(item.id)

                return (
                  <tr key={item.id} style={marcado ? { background: '#eff6ff' } : undefined}>
                    <td style={{ textAlign: 'center' }}>
                      <Form.Check
                        type="checkbox"
                        checked={marcado}
                        onChange={() => toggleUm(item.id)}
                      />
                    </td>
                    <td>{item.id}</td>
                    <td>{item.sku}</td>
                    <td style={{ maxWidth: 240, whiteSpace: 'normal' }}>{descDe(item)}</td>
                    <td>{item.id_pedido}</td>
                    <td>{fmt(item.data_conf, 'dd/MM/yyyy')}</td>
                    <td>{fmt(item.hora_conf, 'HH:mm:ss')}</td>
                    <td>{item.qtde}</td>
                    <td style={{ color: '#94a3b8' }}>{item.qtde_devolvida ?? 0}</td>
                    <td style={{ fontWeight: 600 }}>{saldo}</td>
                    <td>
                      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                        <Form.Control
                          type="number"
                          value={item.vai_devolver}
                          min={1}
                          max={saldo}
                          style={{
                            width: 84,
                            borderColor: vd > 0 && invalido ? '#ef4444' : undefined,
                          }}
                          onChange={(e) => setVaiDevolver(item.id, e.target.value)}
                        />
                        <button
                          type="button"
                          onClick={() => setVaiDevolver(item.id, saldo)}
                          title="Preencher com o saldo"
                          style={miniBtn}
                        >
                          tudo
                        </button>
                      </div>
                    </td>
                    <td>
                      <CustomButton
                        variant="outline-danger"
                        size="sm"
                        onClick={() => pedirConfirmacao([{ ...item, vai_devolver: vd }])}
                        isLoading={isDevolvLoading[item.id]}
                        isLoadingText="Devolvendo..."
                        isDelete
                        isDisabled={invalido}
                      >
                        Estornar
                      </CustomButton>
                    </td>
                  </tr>
                )
              })
            )}
          </tbody>
        </Table>

        {/* ══ Seção: JÁ DEVOLVIDOS ═══════════════════════════════════ */}
        <div style={secHead}>
          <span style={{ ...dot, background: '#22c55e' }} />
          <span style={secTitle}>Já devolvidos</span>
          <span style={{ ...badge, background: '#dcfce7', color: '#166534' }}>
            {jaDevolvidos.length}
          </span>
          {totais.totalDevolvido > 0 && (
            <span style={{ marginLeft: 'auto', fontSize: 12, color: '#166534', fontWeight: 600 }}>
              Total devolvido: {totais.totalDevolvido} un.
            </span>
          )}
        </div>

        <Table striped bordered hover size="sm">
          <thead style={thead}>
            <tr>
              <th>Código</th>
              <th>SKU</th>
              <th>Descrição</th>
              <th>Pedido</th>
              <th>Data</th>
              <th>Hora</th>
              <th>Qtd total</th>
              <th>Qtd devolvida</th>
              <th>Situação</th>
            </tr>
          </thead>
          <tbody>
            {jaDevolvidos.length === 0 ? (
              <tr>
                <td colSpan={9} style={vazio}>
                  {busca ? 'Nenhuma devolução encontrada para essa busca.' : 'Nenhuma devolução registrada ainda.'}
                </td>
              </tr>
            ) : (
              jaDevolvidos.map((item) => {
                const devolvida = Number(item.qtde_devolvida) || 0
                const total = Number(item.qtde) || 0
                const completo = devolvida >= total
                return (
                  <tr key={`dev-${item.id}`} style={{ background: '#f0fdf4' }}>
                    <td>{item.id}</td>
                    <td>{item.sku}</td>
                    <td style={{ maxWidth: 260, whiteSpace: 'normal' }}>{descDe(item)}</td>
                    <td>{item.id_pedido}</td>
                    <td>{fmt(item.data_conf, 'dd/MM/yyyy')}</td>
                    <td>{fmt(item.hora_conf, 'HH:mm:ss')}</td>
                    <td>{total}</td>
                    <td style={{ color: '#166534', fontWeight: 700 }}>{devolvida}</td>
                    <td>
                      <span
                        style={{
                          fontSize: 11, fontWeight: 700, padding: '2px 10px', borderRadius: 99,
                          background: completo ? '#dcfce7' : '#fef3c7',
                          color: completo ? '#166534' : '#92400e',
                        }}
                      >
                        {completo ? 'Devolvido' : 'Parcial'}
                      </span>
                    </td>
                  </tr>
                )
              })
            )}
          </tbody>
        </Table>

        {/* ══ Overlay de confirmação (1 ou vários) ══════════════════ */}
        {confirmLista && (
          <div style={overlay} onClick={cancelarConfirmacao}>
            <div style={confirmCard} onClick={(e) => e.stopPropagation()}>
              <div style={confirmHeader}>
                {confirmLista.length === 1
                  ? 'Confirmar devolução'
                  : `Confirmar devoluções (${confirmLista.length})`}
              </div>

              <div style={{ padding: '14px 16px' }}>
                {confirmLista.length === 1 ? (
                  <>
                    <p style={{ margin: '0 0 12px', color: '#475569', fontSize: 13 }}>
                      Confirme a saída de devolução deste item:
                    </p>
                    <div style={infoBox}>
                      <Linha label="SKU" valor={confirmLista[0].sku} />
                      <Linha label="Descrição" valor={descDe(confirmLista[0])} />
                      <Linha label="Pedido" valor={confirmLista[0].id_pedido} />
                      <Linha label="Qtd a devolver" valor={Number(confirmLista[0].vai_devolver)} destaque />
                      <Linha
                        label="Saldo após"
                        valor={saldoDe(confirmLista[0]) - Number(confirmLista[0].vai_devolver)}
                      />
                    </div>
                  </>
                ) : (
                  <>
                    <p style={{ margin: '0 0 10px', color: '#475569', fontSize: 13 }}>
                      Serão devolvidos {totalUnidadesConfirm} un. em {confirmLista.length} itens:
                    </p>
                    <div style={{ maxHeight: 240, overflowY: 'auto', ...infoBox }}>
                      {confirmLista.map((it) => (
                        <div key={it.id} style={{ display: 'flex', justifyContent: 'space-between', gap: 10, padding: '5px 0', borderBottom: '1px solid #eef0f4' }}>
                          <span style={{ fontSize: 12, color: '#1e293b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            <b>{it.sku}</b> — {descDe(it)}
                          </span>
                          <span style={{ fontSize: 12, fontWeight: 700, color: '#dc2626', whiteSpace: 'nowrap' }}>
                            × {Number(it.vai_devolver)}
                          </span>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>

              <div style={confirmFooter}>
                <Button variant="secondary" size="sm" onClick={cancelarConfirmacao} disabled={processando}>
                  Cancelar
                </Button>
                <CustomButton
                  variant="outline-danger"
                  size="sm"
                  isDelete
                  isLoading={processando}
                  isLoadingText="Devolvendo..."
                  onClick={confirmarEstorno}
                >
                  {confirmLista.length === 1 ? 'Confirmar devolução' : 'Confirmar todas'}
                </CustomButton>
              </div>
            </div>
          </div>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={fecharTudo} disabled={processando}>
          Fechar
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

const Linha = ({ label, valor, destaque }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, padding: '5px 0', borderBottom: '1px solid #eef0f4' }}>
    <span style={{ color: '#64748b', fontSize: 12 }}>{label}</span>
    <span style={{ color: destaque ? '#dc2626' : '#1e293b', fontWeight: destaque ? 700 : 500, fontSize: 13, textAlign: 'right' }}>
      {valor ?? '—'}
    </span>
  </div>
)

const secHead = { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }
const secTitle = { fontSize: 14, fontWeight: 700, color: '#1e293b' }
const dot = { width: 9, height: 9, borderRadius: '50%', display: 'inline-block' }
const badge = {
  fontSize: 11, fontWeight: 700, padding: '1px 9px', borderRadius: 99,
  background: '#fef3c7', color: '#92400e',
}
const chip = { fontSize: 12, fontWeight: 600, padding: '4px 12px', borderRadius: 99, whiteSpace: 'nowrap' }
const thead = { position: 'sticky', top: 0, background: '#f8fafc', zIndex: 1 }
const vazio = { textAlign: 'center', padding: 24, color: '#8892a4' }
const infoBox = { background: '#f8fafc', borderRadius: 8, padding: '4px 12px' }
const bulkBar = {
  display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
  padding: '8px 12px', marginBottom: 10, borderRadius: 8,
  background: '#eff6ff', border: '1px solid #bfdbfe',
}
const miniBtn = {
  border: '1px solid #e2e6ed', background: '#f8fafc', color: '#4f46e5',
  borderRadius: 6, padding: '2px 8px', fontSize: 11, fontWeight: 600, cursor: 'pointer',
}

const overlay = {
  position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  zIndex: 2000, padding: 16,
}
const confirmCard = {
  width: '100%', maxWidth: 420, background: '#fff', borderRadius: 12,
  boxShadow: '0 20px 50px rgba(0,0,0,0.25)', overflow: 'hidden',
}
const confirmHeader = {
  padding: '14px 16px', borderBottom: '1px solid #eef0f4',
  fontSize: '1rem', fontWeight: 700, color: '#1a1d23',
}
const confirmFooter = {
  padding: '12px 16px', borderTop: '1px solid #eef0f4',
  display: 'flex', justifyContent: 'flex-end', gap: 8,
}

export default ModalTable